// File: /src/components/settings/AiEngineSheet.js
import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { AlertCircle, CheckCircle, ChevronRight, Loader2 } from "lucide-react";
import HelpLink from "../help/HelpLink";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useToast } from "../../contexts/ToastContext";
import {
  LLM_PROVIDERS,
  getModelDisplayName,
  validateApiKey,
  getRecommendedModel,
  getProviderGroups,
} from "../../config/llmProviders";
import {
  testGeminiConnection,
  supportsGeminiThinking,
  supportsMinimalThinking,
} from "../../apis/geminiApi";
import { supportsOpenRouterReasoning } from "../../apis/llm/reasoning";
import { openAIRequestLimits } from "../../apis/llm/requestLimits";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChipGrid, Field, MoreOptionsRow, OptionList, SwitchRow } from "../profile/ProfileParts";
import useUiMode from "../../hooks/useUiMode";
import api from "../../services/api";
import {
  canAskForMore,
  describeExpiry,
  describeLeft,
  firstName,
  isOnShare,
  settingsForShare,
  shareModels,
  shareSnapshot,
  shareTitle,
} from "../../utils/creditShares";
import { describeRate, displayCurrency } from "../../utils/money";

export const costReportingNote = (config) => {
  switch (config?.costReporting) {
    case "actual":
      return "Reports the exact cost of every generation, so the figure shown on a session is what you were charged.";
    case "free":
      return "Runs on the Khronos server, so generations cost nothing.";
    default:
      return "Returns token counts but not cost, so generation cost is estimated from published rates. Use OpenRouter for exact figures.";
  }
};

/**
 * Why a draft can't be saved, or null when it can. A provider that needs a
 * key can't run without a well-formed one, so saving it would only break
 * every generation.
 */
export const draftProblem = (draft) => {
  // A mate's shared credits carry their key on the server, not here.
  if (isOnShare(draft)) return null;
  const config = LLM_PROVIDERS[draft.provider];
  if (!config?.apiKeyRequired) return null;
  if (!draft.apiKey) return "missing";
  if (!validateApiKey(draft.provider, draft.apiKey)) return "invalid";
  return null;
};

const USE_CASE_TAGS = [
  ["fast", "Fast"],
  ["balanced", "Balanced"],
  ["quality", "Best"],
];

const NavRow = ({ label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid w-full grid-cols-[auto_minmax(0,1fr)_1rem] items-center gap-2.5 px-3 py-3 text-left transition-colors hover:bg-muted/30"
  >
    <span className="whitespace-nowrap text-sm font-medium">{label}</span>
    <span className="truncate text-right text-[13px]">{value}</span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
  </button>
);

/**
 * AI engine (issue #86): provider, model, thinking, the refine pass and the
 * key, edited as a draft and saved when the sheet closes. A draft that can't
 * run (no key, or one that isn't well-formed) is not saved: the last working
 * setup stays and a toast says why, so closing the sheet can never leave
 * generation broken.
 */
const AiEngineSheet = ({ open, onClose, credits = null }) => {
  const { llmSettings, setLlmSettings, money: savedMoney } = useContext(WorkoutContext);
  const { showToast } = useToast();
  const [settings, setSettings] = useState(llmSettings);
  const [view, setView] = useState("main");
  const [localModels, setLocalModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [error, setError] = useState(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  // Basic mode (#172): the recommended providers and their default model,
  // without the tuning. "More options" shows the rest for this visit.
  const { advanced } = useUiMode();
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (open) setRevealed(false);
  }, [open]);
  const showAll = advanced || revealed;

  // A fresh draft each time the sheet opens, from whatever is saved now
  // (settings can arrive from the account after the page mounted).
  useEffect(() => {
    if (!open) return;
    setSettings(llmSettings);
    setView("main");
    setConnectionStatus(null);
    setError(null);
    credits?.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Credits mates have shared with you, offered as providers of their own.
  const received = (credits?.received || []).filter((share) => share.status === "active");
  const onShare = isOnShare(settings);
  const currentShare = onShare ? received.find((share) => share.id === settings.creditShare.id) : null;
  const shareEnded = onShare && credits && !credits.loading && !currentShare;
  // The ended share itself, if it's still listed: asking for more needs it.
  const endedShare = shareEnded ? (credits.received || []).find((share) => share.id === settings.creditShare.id) : null;
  const [askedMore, setAskedMore] = useState(false);
  const askForMore = async () => {
    try {
      await api.askForMoreCredits(endedShare.id);
      setAskedMore(true);
    } catch (e) {
      showToast({ title: "Couldn't ask", description: e.message });
    }
  };

  // The owner can change which models a share covers. Keep the draft's copy
  // in step so the model list and the request the server checks agree.
  useEffect(() => {
    if (!open || !currentShare) return;
    const fresh = shareSnapshot(currentShare);
    if (JSON.stringify(fresh) === JSON.stringify(settings.creditShare)) return;
    setSettings((prev) => settingsForShare(prev, currentShare));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentShare]);

  const selectedProviderConfig = LLM_PROVIDERS[settings.provider];
  // Prices in the sheet follow the draft's currency, so switching shows at once.
  const money = { ...savedMoney, currency: settings.currency || "AUD" };
  // Basic lists the recommended hosted providers only; the one in use always
  // shows, so nobody loses sight of their own setup.
  const providerGroups = getProviderGroups(LLM_PROVIDERS)
    .filter((g) => showAll || g.key === "recommended")
    .map((g) =>
      showAll
        ? g
        : { ...g, providers: g.providers.filter((p) => !p.isLocal || p.key === settings.provider) },
    );
  const problem = draftProblem(settings);

  const fetchLocalModels = useCallback(async () => {
    setIsLoadingModels(true);
    setError(null);
    try {
      const response = await fetch("/ollama-api/tags");
      if (!response.ok) {
        throw new Error("Can't reach Ollama. Check it's running.");
      }
      // A proxy that can't reach Ollama can answer with an HTML page.
      const data = await response.json().catch(() => null);
      if (!Array.isArray(data?.models)) {
        throw new Error("Can't reach Ollama. Check it's running.");
      }
      const modelNames = data.models.map((m) => m.name);
      setLocalModels(modelNames);

      // Auto-select first model if current selection is invalid
      setSettings((prev) =>
        modelNames.length > 0 && !modelNames.includes(prev.model) ? { ...prev, model: modelNames[0] } : prev,
      );
      setConnectionStatus("connected");
    } catch (err) {
      setError(err.message);
      setLocalModels([]);
      setConnectionStatus("error");
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  // Fetch Ollama models if selected
  useEffect(() => {
    if (open && settings.provider === "ollama") {
      fetchLocalModels();
    } else {
      setLocalModels([]);
    }
  }, [open, fetchLocalModels, settings.provider]);

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus(null);
    setError(null);

    try {
      if (settings.provider === "ollama") {
        await fetchLocalModels();
      } else {
        // Test API key by making a minimal request through the proxy
        const provider = settings.provider;
        let response;

        if (provider === "anthropic" || provider === "minimax") {
          // Anthropic Messages API format (also used by MiniMax)
          const proxyPath = provider === "minimax" ? "/proxy/minimax/v1/messages" : "/proxy/anthropic/messages";
          response = await fetch(proxyPath, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": settings.apiKey,
              "anthropic-version": "2023-06-01",
            },
            body: JSON.stringify({
              model: settings.model,
              max_tokens: 16,
              messages: [{ role: "user", content: "Say OK" }],
            }),
          });
        } else if (provider === "google") {
          await testGeminiConnection({ apiKey: settings.apiKey, model: settings.model });
          response = { ok: true };
        } else {
          // OpenAI-compatible format (openai, deepseek, groq, openrouter)
          const proxyPaths = {
            openai: "/proxy/openai/chat/completions",
            deepseek: "/proxy/deepseek/chat/completions",
            groq: "/proxy/groq/chat/completions",
            openrouter: "/proxy/openrouter/chat/completions",
          };
          response = await fetch(proxyPaths[provider], {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${settings.apiKey}`,
            },
            body: JSON.stringify({
              model: settings.model,
              messages: [{ role: "user", content: "Say OK" }],
              // OpenAI's reasoning models reject `max_tokens` (#165).
              ...openAIRequestLimits(provider, 16),
            }),
          });
        }

        if (response.ok) {
          setConnectionStatus("connected");
        } else {
          const errorBody = await response.json().catch(() => ({}));
          throw new Error(errorBody.error?.message || `HTTP ${response.status}`);
        }
      }
    } catch (err) {
      setConnectionStatus("error");
      setError(err.message);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleProviderChange = (newProvider) => {
    if (String(newProvider).startsWith("share:")) {
      const share = received.find((s) => `share:${s.id}` === newProvider);
      if (share) setSettings(settingsForShare(settings, share));
      setConnectionStatus(null);
      setError(null);
      setView("main");
      return;
    }
    const config = LLM_PROVIDERS[newProvider];
    const newModel = config.defaultModel || config.models[0] || "";
    // Restore saved key for the new provider (if any)
    const savedKey = settings.apiKeys?.[newProvider] || "";
    setSettings({ ...settings, provider: newProvider, model: newModel, apiKey: savedKey, creditShare: null });
    setConnectionStatus(null);
    setError(null);
    setView("main");
  };

  const handleApiKeyChange = (e) => {
    const key = e.target.value.trim();
    const apiKeys = { ...(settings.apiKeys || {}) };
    if (key && settings.provider !== "ollama") {
      apiKeys[settings.provider] = key;
    }
    setSettings({ ...settings, apiKey: key, apiKeys });
    setConnectionStatus(null);
  };

  // The same stored level drives Gemini's `thinkingLevel` and OpenRouter's
  // `reasoning.effort`, so the control belongs on both.
  const thinkingLevelApplies =
    (settings.provider === "google" && supportsGeminiThinking(settings.model)) ||
    (settings.provider === "openrouter" && supportsOpenRouterReasoning(settings.model));

  // Gemini 3.7 removed the "Minimal" thinking level and errors if it is sent.
  // Rather than let a setting saved against an older model fail every
  // generation, drop the option and carry the choice up to "Low".
  // OpenRouter's effort scale starts at "low" outright, so it never offers it.
  const minimalThinkingAvailable =
    settings.provider !== "openrouter" && supportsMinimalThinking(settings.model);

  useEffect(() => {
    if (!minimalThinkingAvailable && settings.geminiThinkingLevel === "MINIMAL") {
      setSettings((prev) => ({ ...prev, geminiThinkingLevel: "LOW" }));
    }
  }, [minimalThinkingAvailable, settings.geminiThinkingLevel]);

  // Save on close. Kept in a ref so a close from any path (Done, swipe,
  // Escape) sees the latest draft.
  const latest = useRef({});
  latest.current = { settings, llmSettings, problem };
  const close = () => {
    const { settings: draft, llmSettings: saved, problem: why } = latest.current;
    if (why) {
      const was = LLM_PROVIDERS[saved.provider]?.label || saved.provider;
      const now = LLM_PROVIDERS[draft.provider]?.label || draft.provider;
      showToast({
        title: `Still using ${was}`,
        description:
          why === "missing"
            ? `${now} needs an API key, so nothing changed.`
            : `That ${now} key doesn't look complete, so nothing changed.`,
      });
    } else if (JSON.stringify(draft) !== JSON.stringify(saved)) {
      setLlmSettings(draft);
    }
    onClose();
  };

  const modelOptions = (() => {
    const names = onShare
      ? shareModels(settings.creditShare)
      : settings.provider === "ollama"
        ? localModels
        : selectedProviderConfig?.models || [];
    const tags = {};
    if (!selectedProviderConfig?.isLocal) {
      for (const [useCase, label] of USE_CASE_TAGS) {
        const m = getRecommendedModel(settings.provider, useCase);
        if (m && !tags[m]) tags[m] = label;
      }
    }
    return names.map((name) => ({
      value: name,
      label: getModelDisplayName(settings.provider, name, money),
      aside: tags[name],
    }));
  })();

  const titles = { main: "AI engine", provider: "Provider", model: "Model" };

  return (
    <DetailSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      title={titles[view]}
      onBack={view === "main" ? undefined : () => setView("main")}
    >
      {view === "provider" && received.length > 0 && (
        <Field label="Shared with you">
          <OptionList
            options={received.map((share) => ({
              value: `share:${share.id}`,
              label: shareTitle(share),
              description: `${describeLeft(share)}, ${describeExpiry(share)}. ${firstName(share.owner.name)} pays and sees which days you use it.`,
              aside: "Shared",
            }))}
            value={onShare ? `share:${settings.creditShare.id}` : null}
            onChange={handleProviderChange}
          />
        </Field>
      )}

      {view === "provider" &&
        providerGroups.map((group) => (
          <Field key={group.key} label={group.label}>
            <OptionList
              options={group.providers.map((p) => ({
                value: p.key,
                label: p.label,
                description: p.description,
                aside: p.isLocal ? "Free" : p.costReporting === "actual" ? "Exact cost" : undefined,
              }))}
              value={onShare ? null : settings.provider}
              onChange={handleProviderChange}
            />
          </Field>
        ))}

      {view === "model" &&
        (isLoadingModels ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading local models…
          </p>
        ) : modelOptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No models found. On the server, run <code className="rounded bg-muted px-1">ollama pull llama3.2</code>.
          </p>
        ) : (
          <OptionList
            options={modelOptions}
            value={settings.model}
            onChange={(model) => {
              setSettings({ ...settings, model });
              setView("main");
            }}
          />
        ))}

      {view === "main" && (
        <>
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
            <NavRow
              label="Provider"
              value={onShare ? shareTitle(settings.creditShare) : selectedProviderConfig?.label || settings.provider}
              onClick={() => setView("provider")}
            />
            {(showAll || !settings.model) && (
              <NavRow
                label="Model"
                value={settings.model ? getModelDisplayName(settings.provider, settings.model, money) : "Choose a model"}
                onClick={() => setView("model")}
              />
            )}
          </div>
          <p className="-mt-3 text-xs leading-snug text-muted-foreground">
            {onShare
              ? currentShare
                ? `On ${firstName(settings.creditShare.ownerName)}'s credits: ${currentShare.limitAmount == null ? "no limit" : describeLeft(currentShare)}, ${describeExpiry(currentShare)}. Their key stays on the server.`
                : `On ${firstName(settings.creditShare.ownerName)}'s shared credits. Their key stays on the server.`
              : costReportingNote(selectedProviderConfig)}{" "}
            <HelpLink topic={onShare ? "ai-options#using-a-mates-credits" : "ai-options"} label="About the AI options" />
          </p>

          {shareEnded && (
            <div className="grid gap-2.5">
              <p className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {firstName(settings.creditShare.ownerName)}&rsquo;s shared credits have ended.{" "}
                  {canAskForMore(endedShare)
                    ? "Ask them for more, or pick another provider to keep generating."
                    : "Pick another provider to keep generating."}
                </span>
              </p>
              {canAskForMore(endedShare) && (
                <Button type="button" variant="outline" className="w-full" disabled={askedMore} onClick={askForMore}>
                  {askedMore
                    ? `Asked ${firstName(settings.creditShare.ownerName)}. They've been told.`
                    : `Ask ${firstName(settings.creditShare.ownerName)} for more`}
                </Button>
              )}
            </div>
          )}

          {!onShare && selectedProviderConfig?.apiKeyRequired && (
            <Field
              label="API key"
              htmlFor="apiKey"
              hint={
                problem === "invalid" ? (
                  <span className="text-destructive">
                    That doesn&rsquo;t look like a full key. Your last working setup stays until this one is right.
                  </span>
                ) : (
                  "Stored on this device and, when you're signed in, with your account. Used only to write your sessions."
                )
              }
            >
              <Input
                type="password"
                id="apiKey"
                name="apiKey"
                autoComplete="off"
                value={settings.apiKey}
                onChange={handleApiKeyChange}
                placeholder={`Paste your ${selectedProviderConfig.label} key`}
                className={problem === "invalid" ? "border-destructive font-mono" : "font-mono"}
              />
            </Field>
          )}

          {showAll && thinkingLevelApplies && (
            <Field label="Thinking" hint="More thinking writes better sessions but takes longer.">
              <ChipGrid
                options={[
                  { value: "OFF", label: "Off" },
                  ...(minimalThinkingAvailable ? [{ value: "MINIMAL", label: "Minimal" }] : []),
                  { value: "LOW", label: "Low" },
                  { value: "MEDIUM", label: "Medium" },
                  { value: "HIGH", label: "High" },
                ]}
                value={settings.geminiThinkingLevel || "MEDIUM"}
                onChange={(v) => setSettings({ ...settings, geminiThinkingLevel: v })}
              />
            </Field>
          )}

          {showAll && (
            <SwitchRow
              id="refine-toggle"
              label="Refine plans"
              description="A second pass fixes sessions that run well over or under your time. Costs one more generation when it runs."
              checked={settings.refine !== false}
              onCheckedChange={(checked) => setSettings({ ...settings, refine: checked })}
            />
          )}

          {showAll && (
            <Field
              label="Show costs in"
              hint={
                settings.currency === "AUD" && displayCurrency(money) !== "AUD"
                  ? "Can't get today's exchange rate, so costs show in US$ for now."
                  : displayCurrency(money) === "AUD"
                    ? `Providers bill in US$. A$ is converted at ${describeRate(money)}.`
                    : "What providers bill in."
              }
            >
              <ChipGrid
                options={[
                  { value: "AUD", label: "A$ (Australian)" },
                  { value: "USD", label: "US$" },
                ]}
                value={settings.currency || "AUD"}
                onChange={(currency) => setSettings({ ...settings, currency })}
              />
            </Field>
          )}

          {!onShare && (
            <div className="grid gap-2">
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={testConnection}
                  disabled={isTestingConnection || (selectedProviderConfig?.apiKeyRequired && !!problem)}
                >
                  {isTestingConnection && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isTestingConnection ? "Testing…" : "Test connection"}
                </Button>
              </div>
              {connectionStatus === "connected" && (
                <p className="flex items-center gap-2 text-sm text-green-500">
                  <CheckCircle className="h-4 w-4" /> Connected. A test request worked.
                </p>
              )}
              {connectionStatus === "error" && (
                <p className="flex items-start gap-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Couldn&rsquo;t connect{error ? `: ${error}` : "."}</span>
                </p>
              )}
              {settings.provider === "ollama" && error && (
                <p className="text-xs leading-snug text-muted-foreground">
                  Ollama runs alongside the Khronos server. If it&rsquo;s down, choose another provider for now.
                </p>
              )}
            </div>
          )}

          {!showAll && <MoreOptionsRow onClick={() => setRevealed(true)} />}
          <p className="text-xs text-muted-foreground">Saves when you close this sheet.</p>
        </>
      )}
    </DetailSheet>
  );
};

export default AiEngineSheet;
